import React, { useEffect, useState } from "react";
import { useRole } from "../context/RoleContext";
import { getAudit, exportUrl } from "../lib/cityPortal";
import { Download } from "lucide-react";

const EVENT_TYPES = [
  { v: "design_review.approved", label: "Design Approved" },
  { v: "design_review.rejected", label: "Design Rejected" },
  { v: "design_review.revisions_requested", label: "Revisions Requested" },
  { v: "design.submitted", label: "Design Submitted" },
  { v: "design.approved", label: "Legacy Design Approval" },
  { v: "consultation.opened", label: "Consultation Opened" },
  { v: "consultation_activated", label: "Consultation Activated" },
  { v: "plans.purchased", label: "Plans Purchased" },
  { v: "plans_purchased", label: "Plans Purchased (new)" },
  { v: "permit.submitted", label: "Permit Submitted" },
  { v: "inspection.logged", label: "Inspection Logged" },
  { v: "milestone_verified", label: "Milestone Verified" },
  { v: "signed_to_dbns", label: "Signed to DBNS" },
  { v: "project.final_record_generated", label: "Final Record Sent" },
  { v: "document_generated", label: "Document Generated" },
  { v: "accela.sync", label: "Accela Sync" },
];

const ACTORS = [
  { v: "all", label: "All" },
  { v: "city_staff", label: "City Staff" },
  { v: "architect", label: "Architect" },
  { v: "owner_builder", label: "Owner / Builder" },
  { v: "system", label: "System" },
];

const NEIGHBORHOODS = [
  { v: "all", label: "All neighborhoods" },
  { v: "Near Eastside", label: "Near Eastside" },
  { v: "Martindale-Brightwood", label: "Martindale-Brightwood" },
  { v: "Other Indianapolis", label: "Other Indianapolis" },
];

export default function CityAudit() {
  const { user } = useRole();
  const [types, setTypes] = useState([]); // empty = all civic actions
  const [actor, setActor] = useState("all");
  const [neighborhood, setNeighborhood] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getAudit(user.id, { types, actor, neighborhood, from, to }).then(
      (d) => alive && setData(d),
    );
    return () => {
      alive = false;
    };
  }, [user?.id, types, actor, neighborhood, from, to]);

  const toggleType = (v) =>
    setTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const csvParams = {
    types: types.join(","),
    actor,
    neighborhood,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  return (
    <section className="animate-fade-in-up" data-testid="city-audit-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>City Staff · Accountability</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Audit Trail.
          </h1>
          <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 4 }}>
            Every design-review decision and project event, with who, what, and when.
          </p>
        </div>
        <a
          href={exportUrl("audit", user?.id, csvParams)}
          className="wn-btn wn-btn-tertiary"
          data-testid="city-audit-export"
        >
          <Download size={14} /> Export to CSV
        </a>
      </div>

      <div className="mt-6 wn-card" style={{ padding: 16 }} data-testid="audit-filter-bar">
        <div className="wn-label">Event type</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip active={types.length === 0} onClick={() => setTypes([])} testId="audit-type-all">
            All civic-scope
          </Chip>
          {EVENT_TYPES.map((t) => (
            <Chip
              key={t.v}
              active={types.includes(t.v)}
              onClick={() => toggleType(t.v)}
              testId={`audit-type-${t.v}`}
            >
              {t.label}
            </Chip>
          ))}
        </div>

        <div className="mt-4 grid sm:grid-cols-4 gap-3">
          <div>
            <label className="wn-label block">Actor</label>
            <select
              className="wn-input"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              data-testid="audit-actor"
            >
              {ACTORS.map((a) => (
                <option key={a.v} value={a.v}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="wn-label block">Neighborhood</label>
            <select
              className="wn-input"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              data-testid="audit-neighborhood"
            >
              {NEIGHBORHOODS.map((n) => (
                <option key={n.v} value={n.v}>{n.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="wn-label block">From</label>
            <input
              type="date"
              className="wn-input tabular-nums"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="audit-from"
            />
          </div>
          <div>
            <label className="wn-label block">To</label>
            <input
              type="date"
              className="wn-input tabular-nums"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="audit-to"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 wn-card" style={{ padding: 0, overflow: "hidden" }} data-testid="audit-table-card">
        <table className="w-full" style={{ borderCollapse: "collapse" }} data-testid="audit-table">
          <thead>
            <tr style={{ background: "#1B1F4B", color: "#FBFAFD" }}>
              <th style={th}>Timestamp</th>
              <th style={th}>Actor</th>
              <th style={th}>Action</th>
              <th style={th}>Subject</th>
              <th style={th}>Notes</th>
              <th style={th}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {!data && (
              <tr>
                <td colSpan={6} style={{ padding: 24, color: "#6C6B85", fontStyle: "italic", textAlign: "center" }}>
                  Loading audit entries…
                </td>
              </tr>
            )}
            {data && data.items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 24, color: "#6C6B85", textAlign: "center" }} data-testid="audit-empty">
                  No events match this filter.
                </td>
              </tr>
            )}
            {data?.items.map((a, i) => (
              <tr
                key={a.id || i}
                style={{ background: i % 2 === 0 ? "#FBFAFD" : "#F8F7FB" }}
                data-testid={`audit-row-${a.id || i}`}
              >
                <td style={{ ...td, fontWeight: 600 }} className="tabular-nums">
                  {formatTs(a.at)}
                </td>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "#1B1F4B" }}>
                    {a.actor?.name || a.actor_id || "—"}
                  </div>
                  {a.actor?.role && (
                    <div className="wn-label" style={{ fontSize: 10 }}>{a.actor.role.replace(/_/g, " ")}</div>
                  )}
                </td>
                <td style={{ ...td, fontWeight: 600 }}>{prettyAction(a.action)}</td>
                <td style={td}>
                  <div style={{ fontSize: 12.5 }}>{a.record_type}</div>
                  <div className="tabular-nums" style={{ color: "#6C6B85", fontSize: 11 }}>
                    {a.record_id}
                  </div>
                </td>
                <td style={{ ...td, color: "#272636" }} title={a.note}>
                  <div
                    style={{
                      maxWidth: 280,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {a.note || "—"}
                  </div>
                </td>
                <td style={{ ...td, color: "#3A5563" }} className="tabular-nums">
                  {a.record_id}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const th = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};
const td = {
  padding: "10px 14px",
  fontSize: 12.5,
  color: "#272636",
  borderTop: "1px solid #D9D8E4",
  verticalAlign: "top",
};

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return iso;
  }
}

function prettyAction(a) {
  if (!a) return "—";
  return a
    .replace(/_/g, " ")
    .replace(/\./g, " → ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "3px 9px",
        borderRadius: 999,
        border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
        background: active ? "#1B1F4B" : "transparent",
        color: active ? "#FBFAFD" : "#272636",
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
